'use client';

import { lazy, Suspense, type ReactNode } from 'react';

import type { ResponderCapabilityOverrides } from './shared-responder-v2-capabilities.context';
import ResponderGateLoading from './shared-responder-v2-gate-loading';
import type { ResponderSessionContact } from './shared-responder-v2-insight-session.hooks';
import type { ResponderProjectInput } from './shared-responder-v2-project.types';

/*
 * Lazy so the whole V2 tree (`-core`/`-question`/`-shell`/`-media` plus
 * `qrcode.react` and `@dnd-kit`) is neither downloaded nor module-evaluated
 * for a legacy project. The `project` type import above is erased at compile
 * time, so it does not pull the adapter (which reads it) into this eagerly
 * loaded module.
 */
const loadResponderV2 = () => import('./shared-responder-v2');

const ResponderV2 = lazy(loadResponderV2);

export function preloadResponderV2(): void {
  loadResponderV2().catch(() => undefined);
}

/**
 * Separately lazy so an expired session ships a card, not the whole responder —
 * and so `-shell` stays out of the eagerly loaded graph on the legacy path.
 */
const ResponderV2Expired = lazy(() => import('./shared-responder-v2-expired'));

interface Props {
  isEligible: boolean;
  /** Forwarded to V2 only; legacy (`children`) never reads this. */
  project?: ResponderProjectInput | null;
  /** Forwarded to V2 only. False inside `/manager-embed`. */
  allowVoice?: boolean;
  hostPageUrl?: string | null;
  /** No session to answer: renders V2's expired screen instead of the responder. */
  isExpired?: boolean;
  /** Carries the gateway. Without one, V2 renders its placeholder state. */
  capabilities?: ResponderCapabilityOverrides;
  /** True in the builder preview, which is the author's own rehearsal rather than a respondent session. */
  isPreviewSession?: boolean;
  contact?: ResponderSessionContact;
  /** The screener's `projectSessionKey`; omitted when the project has none. */
  sessionKey?: string | null;
  /** Screener seam: the key was rejected, the caller reruns the screener. */
  onScreenerKeyRejected?: () => void;
  /**
   * The legacy responder, rendered when the project is not eligible. Optional
   * because the builder preview has already decided the project is typeless and
   * has no legacy tree to fall back to — it still comes through this gate to
   * keep the whole V2 chunk behind one lazy boundary.
   */
  children?: ReactNode;
}

/**
 * Chooses between the legacy responder and V2 on eligibility alone —
 * deliberately no feature flag.
 *
 * `isEligible` is `project.type === null`, and only the V2 builder creates a
 * typeless project, so eligibility already *is* the builder rollout decision —
 * recorded per project at creation time rather than re-evaluated per viewer.
 * A responder-side flag cannot improve on that and made things worse twice
 * over: PostHog is never `identify`-ed in `apps/survey`, so any flag here is
 * evaluated against an anonymous distinct_id unrelated to the project owner;
 * and an unreachable PostHog (ad blockers, which responders run heavily) would
 * fall back to legacy, which cannot render a typeless project correctly —
 * `getTypeOfRenderer` never returns `usabilityNext` without a project type, so
 * URL and Task questions lose their only advance control. Rollback is a deploy;
 * the builder flag remains the switch that stops new typeless projects.
 *
 * Legacy arrives as `children` rather than a render prop because all four
 * responder route views are Server Components: a function prop cannot cross the
 * server/client boundary. The cost is that the legacy element object is created
 * even when V2 wins — element creation, not rendering — and the benefit is that
 * a reviewer can see at the call site that an ineligible project renders
 * exactly the tree it renders today.
 */
export function ResponderV2Gate({
  isEligible,
  project = null,
  allowVoice = false,
  hostPageUrl = null,
  isExpired = false,
  capabilities,
  isPreviewSession = false,
  contact,
  sessionKey = null,
  onScreenerKeyRejected,
  children,
}: Props) {
  if (!isEligible) {
    return children ?? null;
  }

  return (
    <Suspense fallback={<ResponderGateLoading />}>
      {isExpired ? (
        <ResponderV2Expired />
      ) : (
        <ResponderV2
          allowVoice={allowVoice}
          capabilities={capabilities}
          contact={contact}
          hostPageUrl={hostPageUrl}
          isPreviewSession={isPreviewSession}
          onScreenerKeyRejected={onScreenerKeyRejected}
          project={project}
          sessionKey={sessionKey}
        />
      )}
    </Suspense>
  );
}
